import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";
import { ChevronRight } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import OnboardingStep1 from "@/components/onboarding/OnboardingStep1";
import OnboardingStep2 from "@/components/onboarding/OnboardingStep2";
import { BodyResultCard } from "@/components/onboarding/BodyResultCard";

const GEO_TIMEOUT_MS = 6000;

const STEP_TITLES = {
  1: "Let's get to know you",
  2: "Your silhouette",
  3: "You're all set",
};

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    gender: "female",
    age: "",
  });
  const [bodyMode, setBodyMode] = useState("photo");
  const [meas, setMeas] = useState({ height_cm: "", weight_kg: "", shoulder_cm: "", waist_cm: "", hip_cm: "" });
  const [photoFile, setPhotoFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [bodyResult, setBodyResult] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.put("/profile", { lat: pos.coords.latitude, lon: pos.coords.longitude });
        } catch (err) {
          console.error("save geolocation failed", err);
        }
      },
      (err) => console.warn("geolocation denied", err),
      { timeout: GEO_TIMEOUT_MS, enableHighAccuracy: false }
    );
  }, []);

  const saveStep1 = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name");
    setSaving(true);
    try {
      await api.put("/profile", {
        name: form.name,
        gender: form.gender,
        age: form.age ? Number(form.age) : null,
      });
      setStep(2);
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return toast.error("Choose a full-body photo");
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", photoFile);
      const { data } = await api.post("/profile/body-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBodyResult(data);
      toast.success("Body silhouette estimated");
    } catch (err) {
      console.error("body photo analyze failed", err);
      toast.error("Could not analyze photo. Try measurements instead.");
      setBodyMode("measure");
    } finally {
      setAnalyzing(false);
    }
  };

  const saveMeasurements = async () => {
    const payload = {};
    Object.entries(meas).forEach(([k, v]) => {
      if (v) payload[k] = Number(v);
    });
    if (!payload.shoulder_cm || !payload.waist_cm || !payload.hip_cm) {
      return toast.error("Please add shoulder, waist and hip");
    }
    setAnalyzing(true);
    try {
      const { data } = await api.put("/profile", payload);
      setBodyResult({ body_type: data.body_type, body_notes: data.body_notes });
      toast.success("Body silhouette derived");
    } finally {
      setAnalyzing(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { onboarded: true });
      await refresh();
      navigate("/home", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FBF9F6] grain">
      <div className="max-w-md mx-auto px-6 pt-14 pb-16">
        <BrandMark className="mb-6" />
        <div className="text-xs font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54] mb-4">
          Step {step} of 3
        </div>
        <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight tracking-tight text-stone-900 mb-8">
          {STEP_TITLES[step]}
        </h1>

        {step === 1 && (
          <OnboardingStep1 form={form} setForm={setForm} saving={saving} onNext={saveStep1} />
        )}

        {step === 2 && !bodyResult && (
          <OnboardingStep2
            bodyMode={bodyMode}
            setBodyMode={setBodyMode}
            photoFile={photoFile}
            setPhotoFile={setPhotoFile}
            meas={meas}
            setMeas={setMeas}
            analyzing={analyzing}
            onPhotoUpload={uploadPhoto}
            onSaveMeasurements={saveMeasurements}
          />
        )}

        {step === 2 && bodyResult && (
          <BodyResultCard bodyResult={bodyResult} onNext={() => setStep(3)} />
        )}

        {step === 3 && (
          <div className="flex flex-col gap-6">
            <p className="font-[Manrope] text-base text-stone-700 leading-relaxed">
              Next, add at least <span className="text-[#6B7A54] font-semibold">5 items</span> to your closet.
              Once you cross five, we&rsquo;ll unlock your first suggestion.
            </p>
            <button data-testid={IDS.onbFinish} onClick={finish} disabled={saving} className="btn-primary">
              Start my closet <ChevronRight size={16} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
